import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { ArrowLeft, Mic, Save, Plus, X, BookOpen, BarChart3, Pencil } from 'lucide-react';
import { toast } from 'sonner';

const VOICES = [
  { value: 'twilio_alice',  label: 'Twilio · Alice (default)' },
  { value: 'openai_alloy',  label: 'Polly · Joanna (neutral, balanced)' },
  { value: 'openai_echo',   label: 'Polly · Matthew (warm, masculine)' },
  { value: 'openai_fable',  label: 'Polly · Brian (storyteller, British)' },
  { value: 'openai_nova',   label: 'Polly · Ruth (bright, feminine)' },
];

const newId = () => `kb_${Date.now()}_${Math.floor(Math.random() * 1e6)}`;

const VoiceAgentTrainingPage = () => {
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const [training, setTraining] = useState({
    system_prompt: '',
    do_say: [],
    dont_say: [],
    knowledge_entries: [],
    voice: 'twilio_alice',
  });
  const [newDo, setNewDo] = useState('');
  const [newDont, setNewDont] = useState('');
  const [newKbTitle, setNewKbTitle] = useState('');
  const [newKbBody, setNewKbBody] = useState('');
  const [editingKbId, setEditingKbId] = useState(null);
  const [saving, setSaving] = useState(false);

  const isAdmin = user?.role === 'admin';
  // Special users with `admin_ai_voice_agent` toggle get VIEW access.
  // Save buttons + form controls are disabled below for non-admins.
  const canAccess = isAdmin || (user?.feature_toggles || {}).admin_ai_voice_agent === true;
  const canEdit = isAdmin;

  useEffect(() => {
    if (!canAccess) return;
    api.get('/admin/ai-voice-training').then((r) => setTraining({
      system_prompt: r.data?.system_prompt || '',
      do_say: r.data?.do_say || [],
      dont_say: r.data?.dont_say || [],
      knowledge_entries: r.data?.knowledge_entries || [],
      voice: r.data?.voice || 'twilio_alice',
    })).catch(() => {});
  }, [canAccess]); // eslint-disable-line

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/admin/ai-voice-training', training);
      toast.success('Voice agent training saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const addKbEntry = () => {
    const title = newKbTitle.trim();
    const body = newKbBody.trim();
    if (!title && !body) return;
    setTraining((t) => ({
      ...t,
      knowledge_entries: [
        ...(t.knowledge_entries || []),
        { id: newId(), title: title || 'Note', body, created_at: new Date().toISOString() },
      ],
    }));
    setNewKbTitle('');
    setNewKbBody('');
  };

  const updateKbEntry = (id, patch) => {
    setTraining((t) => ({
      ...t,
      knowledge_entries: (t.knowledge_entries || []).map((e) => (e.id === id ? { ...e, ...patch } : e)),
    }));
  };

  const removeKbEntry = (id) => {
    setTraining((t) => ({
      ...t,
      knowledge_entries: (t.knowledge_entries || []).filter((e) => e.id !== id),
    }));
  };

  if (!canAccess) return <div className="p-8 text-center">Admin only</div>;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] pb-24" data-testid="voice-agent-training-page">
      <div className="max-w-3xl mx-auto p-3">
        <div className="flex items-center gap-2 mb-3">
          <Button size="icon" variant="ghost" onClick={() => navigate('/admin/ai-training')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Mic className="w-6 h-6 text-[#3b82f6]" />
            AI Voice Agent
          </h1>
          <Button
            variant="outline"
            size="sm"
            className="ml-auto"
            onClick={() => navigate('/admin/ai-voice-dashboard')}
            data-testid="open-voice-dashboard"
          >
            <BarChart3 className="w-4 h-4 mr-1.5" />
            Dashboard
          </Button>
        </div>

        <Card className="rounded-2xl mb-3">
          <CardContent className="pt-4 text-sm text-muted-foreground">
            {!canEdit && (
              <div className="rounded-md bg-amber-50 border border-amber-200 dark:bg-amber-950/30 dark:border-amber-700 p-2 mb-2 text-[11px] text-amber-700 dark:text-amber-300">
                Read-only — AI agent training and toggles can only be changed by a full admin.
              </div>
            )}
            Train how the IVR voice agent (Key 4 on the Phone Routing menu) speaks to callers. Pick a voice, write a system prompt, and add do/don't-say guardrails plus reference knowledge cards.
          </CardContent>
        </Card>

        <fieldset disabled={!canEdit} className={canEdit ? '' : 'opacity-70 pointer-events-none'} data-testid="voice-training-fieldset">

        <Card className="rounded-2xl mb-3">
          <CardHeader><CardTitle className="text-base">Voice</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {VOICES.map((v) => (
              <label key={v.value} className={`flex items-center gap-3 p-2 rounded-lg border cursor-pointer ${training.voice === v.value ? 'border-[#3b82f6] bg-[#3b82f6]/5' : 'border-gray-200 dark:border-gray-700'}`}>
                <input type="radio" name="voice" checked={training.voice === v.value} onChange={() => setTraining({ ...training, voice: v.value })} data-testid={`voice-${v.value}`} />
                <span className="text-sm flex-1">{v.label}</span>
              </label>
            ))}
          </CardContent>
        </Card>

        <Card className="rounded-2xl mb-3">
          <CardHeader><CardTitle className="text-base">System Prompt</CardTitle></CardHeader>
          <CardContent>
            <Textarea
              value={training.system_prompt}
              onChange={(e) => setTraining({ ...training, system_prompt: e.target.value })}
              placeholder="You are a friendly, professional phone agent for The Uplift…"
              className="min-h-[120px]"
              data-testid="voice-system-prompt"
            />
          </CardContent>
        </Card>

        <Card className="rounded-2xl mb-3">
          <CardHeader><CardTitle className="text-base">Do say</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {(training.do_say || []).map((s, i) => (
              <div key={i} className="flex items-center gap-2 px-2 py-1.5 rounded bg-green-50 dark:bg-green-900/20">
                <span className="flex-1 text-sm">{s}</span>
                <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => setTraining({ ...training, do_say: training.do_say.filter((_, j) => j !== i) })}><X className="w-3 h-3" /></Button>
              </div>
            ))}
            <div className="flex gap-2">
              <Input value={newDo} onChange={(e) => setNewDo(e.target.value)} placeholder='e.g. "Always ask the caller for their full name first"' className="text-sm" />
              <Button size="sm" variant="outline" onClick={() => { if (newDo.trim()) { setTraining({ ...training, do_say: [...(training.do_say || []), newDo.trim()] }); setNewDo(''); } }}><Plus className="w-3.5 h-3.5" /></Button>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl mb-3">
          <CardHeader><CardTitle className="text-base">Don't say</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {(training.dont_say || []).map((s, i) => (
              <div key={i} className="flex items-center gap-2 px-2 py-1.5 rounded bg-red-50 dark:bg-red-900/20">
                <span className="flex-1 text-sm">{s}</span>
                <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => setTraining({ ...training, dont_say: training.dont_say.filter((_, j) => j !== i) })}><X className="w-3 h-3" /></Button>
              </div>
            ))}
            <div className="flex gap-2">
              <Input value={newDont} onChange={(e) => setNewDont(e.target.value)} placeholder='e.g. "Never quote prices over the phone"' className="text-sm" />
              <Button size="sm" variant="outline" onClick={() => { if (newDont.trim()) { setTraining({ ...training, dont_say: [...(training.dont_say || []), newDont.trim()] }); setNewDont(''); } }}><Plus className="w-3.5 h-3.5" /></Button>
            </div>
          </CardContent>
        </Card>

        {/* Knowledge — one card per entry, addable/editable/deletable */}
        <Card className="rounded-2xl mb-3">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-[#3b82f6]" />
              Reference knowledge
              <span className="text-xs text-muted-foreground font-normal ml-auto">
                {(training.knowledge_entries || []).length} card{(training.knowledge_entries || []).length === 1 ? '' : 's'}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {(training.knowledge_entries || []).length === 0 && (
              <p className="text-xs text-muted-foreground text-center py-2">
                No reference cards yet. Add one below — each card becomes its own section the AI can pull from.
              </p>
            )}
            {(training.knowledge_entries || []).map((entry) => {
              const editing = editingKbId === entry.id;
              return (
                <div key={entry.id} className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white/60 dark:bg-[#1a1a1a]/60 p-3" data-testid={`kb-card-${entry.id}`}>
                  {editing ? (
                    <div className="space-y-2">
                      <Input
                        value={entry.title}
                        onChange={(e) => updateKbEntry(entry.id, { title: e.target.value })}
                        placeholder="Card title (e.g. Hours)"
                        className="text-sm font-medium"
                      />
                      <Textarea
                        value={entry.body}
                        onChange={(e) => updateKbEntry(entry.id, { body: e.target.value })}
                        placeholder="Card body — the AI will quote from this when relevant"
                        className="min-h-[100px] text-sm"
                      />
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="ghost" onClick={() => setEditingKbId(null)}>Done</Button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-start gap-2 mb-1.5">
                        <p className="text-sm font-semibold flex-1 break-words">{entry.title || 'Untitled'}</p>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-6 w-6"
                          onClick={() => setEditingKbId(entry.id)}
                          data-testid={`kb-edit-${entry.id}`}
                        >
                          <Pencil className="w-3 h-3" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-6 w-6 text-red-500 hover:text-red-600"
                          onClick={() => removeKbEntry(entry.id)}
                          data-testid={`kb-delete-${entry.id}`}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                      <p className="text-xs whitespace-pre-wrap break-words text-muted-foreground">
                        {entry.body || <em>(empty)</em>}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}

            {/* New entry composer */}
            <div className="rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-700 p-3 space-y-2 bg-white/30 dark:bg-[#0d0d0d]/30">
              <Input
                value={newKbTitle}
                onChange={(e) => setNewKbTitle(e.target.value)}
                placeholder="New card title (e.g. Hours, FAQs, Handoff rules)"
                className="text-sm"
                data-testid="kb-new-title"
              />
              <Textarea
                value={newKbBody}
                onChange={(e) => setNewKbBody(e.target.value)}
                placeholder="Card body…"
                className="min-h-[80px] text-sm"
                data-testid="kb-new-body"
              />
              <div className="flex justify-end">
                <Button size="sm" onClick={addKbEntry} disabled={!newKbBody.trim() && !newKbTitle.trim()} data-testid="kb-add-btn">
                  <Plus className="w-3.5 h-3.5 mr-1.5" />
                  Add knowledge card
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
        </fieldset>

        <Button onClick={save} disabled={saving || !canEdit} className="bg-[#3b82f6] hover:bg-[#2563eb] w-full" data-testid="voice-training-save">
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving…' : (canEdit ? 'Save training' : 'Save (admin only)')}
        </Button>
      </div>
    </div>
  );
};

export default VoiceAgentTrainingPage;
